import "@aws-amplify/ui-react/styles.css";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Amplify } from "aws-amplify";
import * as ReactDOM from "react-dom";
import { createStore, applyMiddleware, compose } from "redux";
import { createEpicMiddleware } from "redux-observable";
import { ajax } from "rxjs/observable/dom/ajax";

import { queryClient } from "@api/queryClient";

import { RollbarProvider } from "@shared/components";
import { ZodTranslationProvider } from "@shared/hooks/providers/ZodTranslationProvider";
import { Provider } from "@shared/libs/react-intl-redux";

import "../node_modules/@fortawesome/fontawesome-free/css/all.min.css";
import "../node_modules/@fortawesome/fontawesome-free/css/v4-shims.min.css";
import {
  i18nGetAction,
  i18nSetCookieAction,
  applicantInitialGetAction,
  applicantSharingGetAction,
  applicantIdSharingGetAction,
} from "./actions";
import App from "./components/user/App";
import { epics } from "./epics";
import "./indexUsers.scss";
import { applicantReducers } from "./reducers";
import { pushVariables, DataLayerValues } from "./shared/services/analytics";
import { activate as activateIntercom } from "./shared/services/intercom";
import Logger from "./shared/services/logger";
import "./shared/services/rxjsBuilder";
import { sessionStorage } from "./shared/services/storage";
import { parseApplicantURL } from "./shared/services/urlParser";
import { version } from "./shared/services/version";

// set container name for tracking
pushVariables({ appName: "applicant" } as DataLayerValues);

// boot intercom widget.
activateIntercom({
  alignment: "left",
  horizontal_padding: 70,
  vertical_padding: 20,
});

Logger.info("Running with user mode.");

const composeEnhancers =
  (process.env.NODE_ENV === "development" &&
    window &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) ||
  compose;

const epicOptions = { dependencies: { ajax } };
const middleware =
  process.env.NODE_ENV !== "production"
    ? // Redux middleware that detects mutations between and outside redux dispatches.
      [
        require("redux-immutable-state-invariant").default(),
        createEpicMiddleware(epics, epicOptions),
      ]
    : [createEpicMiddleware(epics, epicOptions)];

const store = createStore(
  applicantReducers,
  composeEnhancers(applyMiddleware(...middleware)),
);

// parse URL string to pick up token.
const matches = parseApplicantURL(window.location.pathname);

// get initial state data
const lang =
  sessionStorage.getItem("language") ||
  (navigator && navigator.language.split("-")[0]);

store.dispatch(i18nGetAction(lang));
store.dispatch(i18nSetCookieAction(lang));

if (matches !== null) {
  const [, type, token] = matches;
  // NOTE:
  //   If fetching initial data at UNSAFE_componentWillMount on the child component, the child component gets mounted twice for some reason.
  //   I needed to parse URL string the above for scrubbing token. that issue seems to fix React v16 somehow.
  // See:
  //   https://github.com/facebook/react/issues/7632
  switch (type) {
    case "exams":
      store.dispatch(applicantInitialGetAction.request(token));
      break;
    case "share":
      store.dispatch(applicantSharingGetAction.request(token));
      break;
    case "idshare":
      store.dispatch(applicantIdSharingGetAction.request(token));
      break;
    default:
  }
}

Amplify.configure({
  Auth: {
    Cognito: {
      identityPoolId:
        process.env.REACT_APP_FACE_LIVENESS_IDENTITY_POOL_ID || "", // track-test-face-liveness-identity-pool
      allowGuestAccess: true,
    },
  },
});

/**
 * Render
 */
ReactDOM.render(
  <RollbarProvider>
    {/* need to update some type definitions */}
    {/* @ts-ignore */}
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ZodTranslationProvider>
          <App />
          <ReactQueryDevtools initialIsOpen={false} />
        </ZodTranslationProvider>
      </QueryClientProvider>
    </Provider>
  </RollbarProvider>,
  document.getElementById("root") as HTMLElement,
);

Logger.info("env", process.env);
Logger.info("version", version);
